import {addDoc, collection, getFirestore, serverTimestamp} from "firebase/firestore";
import {getAuth} from "firebase/auth";
import Firebase from "../Firebase/Firebase";

const db   = getFirestore(Firebase);
const auth = getAuth(Firebase);

export type AuditAction =
    | 'admin_login'
    | 'admin_logout'
    | 'admin_granted'
    | 'admin_role_changed'
    | 'admin_disabled'
    | 'user_status_changed'
    | 'user_suspended'
    | 'seller_verified'
    | 'seller_suspended'
    | 'seller_edited'
    | 'product_status_changed'
    | 'product_featured'
    | 'product_unfeatured'
    | 'product_appeal_approved'
    | 'product_appeal_dismissed'
    | 'order_status_changed'
    | 'payout_triggered'
    | 'refund_triggered'
    | 'promotion_approved'
    | 'promotion_rejected'
    | 'promotion_stopped'
    | 'content_banner_added'
    | 'content_banner_deleted'
    | 'content_category_added'
    | 'content_section_added'
    | 'settings_changed'
    | 'password_changed'
    | 'email_changed';

export interface AuditDetails {
    targetId?:   string;
    targetName?: string;
    targetType?: 'user' | 'product' | 'order' | 'promotion' | 'payout' | 'content' | 'settings' | 'admin';
    from?:       string;
    to?:         string;
    note?:       string;
}

export const ACTION_LABELS: Record<AuditAction, string> = {
    admin_login:            'Admin Login',
    admin_logout:           'Admin Logout',
    admin_granted:          'Admin Access Granted',
    admin_role_changed:     'Admin Role Changed',
    admin_disabled:         'Admin Disabled',
    user_status_changed:    'User Status Changed',
    user_suspended:         'User Suspended',
    seller_verified:        'Seller Verified',
    seller_suspended:       'Seller Suspended',
    seller_edited:          'Seller Profile Edited',
    product_status_changed: 'Product Status Changed',
    product_featured:       'Product Featured',
    product_unfeatured:     'Product Unfeatured',
    product_appeal_approved:  'Product Appeal Approved',
    product_appeal_dismissed: 'Product Appeal Dismissed',
    order_status_changed:   'Order Status Changed',
    payout_triggered:       'Payout Triggered',
    refund_triggered:       'Refund Triggered',
    promotion_approved:     'Promotion Approved',
    promotion_rejected:     'Promotion Rejected',
    promotion_stopped:      'Promotion Stopped',
    content_banner_added:   'Banner Added',
    content_banner_deleted: 'Banner Deleted',
    content_category_added: 'Category Added',
    content_section_added:  'Section Added',
    settings_changed:       'Settings Changed',
    password_changed:       'Password Changed',
    email_changed:          'Email Changed',
};

export const ACTION_SEVERITY: Record<AuditAction, 'info' | 'warning' | 'success' | 'danger'> = {
    admin_login:            'info',
    admin_logout:           'info',
    admin_granted:          'warning',
    admin_role_changed:     'warning',
    admin_disabled:         'danger',
    user_status_changed:    'warning',
    user_suspended:         'warning',
    seller_verified:        'success',
    seller_suspended:       'danger',
    seller_edited:          'info',
    product_status_changed: 'warning',
    product_featured:       'info',
    product_unfeatured:     'info',
    product_appeal_approved:  'success',
    product_appeal_dismissed: 'danger',
    order_status_changed:   'info',
    payout_triggered:       'success',
    refund_triggered:       'warning',
    promotion_approved:     'success',
    promotion_rejected:     'danger',
    promotion_stopped:      'warning',
    content_banner_added:   'info',
    content_banner_deleted: 'warning',
    content_category_added: 'info',
    content_section_added:  'info',
    settings_changed:       'warning',
    password_changed:       'warning',
    email_changed:          'warning',
};

/**
 * Log an admin action to Firestore. Fire-and-forget — never throws.
 * Call this after every significant admin action.
 */
export const logAdminAction = async (
    action: AuditAction,
    details: AuditDetails = {}
): Promise<void> => {
    const user = auth.currentUser;
    if (!user) return;
    try {
        await addDoc(collection(db, 'adminAuditLog'), {
            adminUid:   user.uid,
            adminEmail: user.email ?? '',
            action,
            ...details,
            timestamp:  serverTimestamp(),
        });
    } catch (_) {
        // Never disrupt the user action if audit logging fails
    }
};
